import React, { useState, useEffect, useMemo } from 'react';
import { apiService } from '../services/api';
import {
  Plus, Search, Pencil, Trash2, Shield, ChevronDown, ChevronUp, X,
  ToggleLeft, ToggleRight, Copy, AlertTriangle, CheckCircle2, Wrench,
} from 'lucide-react';

interface ComplianceRule {
  rule_id: string;
  title: string;
  description?: string;
  category: string;
  check_type: string;
  severity: string;
  platform?: string;
  device_type?: string;
  benchmark_ref?: string;
  check_command?: string;
  osquery_query_name?: string;
  expected_value?: string;
  remediation_script?: string;
  remediation_steps?: string;
  enabled: boolean;
  auto_remediate: boolean;
  created_at?: string;
  updated_at?: string;
}

const CATEGORIES = [
  { id: 'linux-hardening', name: 'Linux Hardening', color: 'bg-orange-100 text-orange-700' },
  { id: 'windows-server', name: 'Windows Server', color: 'bg-blue-100 text-blue-700' },
  { id: 'web-tier', name: 'Web Tier', color: 'bg-purple-100 text-purple-700' },
  { id: 'database', name: 'Database', color: 'bg-emerald-100 text-emerald-700' },
  { id: 'cloud-assets', name: 'Cloud Assets', color: 'bg-cyan-100 text-cyan-700' },
];

const CHECK_TYPES = [
  'network', 'security', 'users', 'file-integrity', 'authentication',
  'access-control', 'encryption', 'audit', 'backup', 'iam', 'logging',
];

const SEVERITIES = [
  { id: 'critical', color: 'bg-red-100 text-red-700' },
  { id: 'high', color: 'bg-orange-100 text-orange-700' },
  { id: 'medium', color: 'bg-amber-100 text-amber-700' },
  { id: 'low', color: 'bg-slate-100 text-slate-600' },
];

const PLATFORMS = ['linux', 'windows', 'macos', 'all'];
const DEVICE_TYPES = ['server', 'desktop', 'laptop', 'all'];

const EMPTY_RULE = {
  title: '',
  description: '',
  category: 'linux-hardening',
  check_type: 'security',
  severity: 'medium',
  platform: 'all',
  device_type: 'all',
  benchmark_ref: '',
  check_command: '',
  osquery_query_name: '',
  expected_value: '',
  remediation_script: '',
  remediation_steps: '',
  enabled: true,
  auto_remediate: false,
};

const ComplianceRulesSettings: React.FC = () => {
  const [rules, setRules] = useState<ComplianceRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterSeverity, setFilterSeverity] = useState<string>('all');
  const [filterEnabled, setFilterEnabled] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<ComplianceRule | null>(null);
  const [formData, setFormData] = useState(EMPTY_RULE);
  const [expandedRuleId, setExpandedRuleId] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  useEffect(() => { loadRules(); }, []);

  useEffect(() => {
    if (successMsg) {
      const t = setTimeout(() => setSuccessMsg(null), 4000);
      return () => clearTimeout(t);
    }
  }, [successMsg]);

  const loadRules = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getComplianceRules({ limit: 500 });
      setRules(data?.items || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load rules');
    } finally {
      setLoading(false);
    }
  };

  const filteredRules = useMemo(() => {
    let list = rules;
    if (filterCategory !== 'all') list = list.filter((r) => r.category === filterCategory);
    if (filterSeverity !== 'all') list = list.filter((r) => r.severity === filterSeverity);
    if (filterEnabled !== 'all') list = list.filter((r) => (filterEnabled === 'enabled' ? r.enabled : !r.enabled));
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (r) =>
          r.title.toLowerCase().includes(q) ||
          (r.description || '').toLowerCase().includes(q) ||
          (r.benchmark_ref || '').toLowerCase().includes(q) ||
          r.check_type.toLowerCase().includes(q)
      );
    }
    return list.sort((a, b) => {
      const catOrder = CATEGORIES.findIndex((c) => c.id === a.category) - CATEGORIES.findIndex((c) => c.id === b.category);
      if (catOrder !== 0) return catOrder;
      return a.title.localeCompare(b.title);
    });
  }, [rules, filterCategory, filterSeverity, filterEnabled, searchQuery]);

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    rules.forEach((r) => { counts[r.category] = (counts[r.category] || 0) + 1; });
    return counts;
  }, [rules]);

  const openCreateModal = () => {
    setEditingRule(null);
    setFormData({ ...EMPTY_RULE });
    setModalOpen(true);
  };

  const openEditModal = (rule: ComplianceRule) => {
    setEditingRule(rule);
    setFormData({
      title: rule.title,
      description: rule.description || '',
      category: rule.category,
      check_type: rule.check_type,
      severity: rule.severity,
      platform: rule.platform || 'all',
      device_type: rule.device_type || 'all',
      benchmark_ref: rule.benchmark_ref || '',
      check_command: rule.check_command || '',
      osquery_query_name: rule.osquery_query_name || '',
      expected_value: rule.expected_value || '',
      remediation_script: rule.remediation_script || '',
      remediation_steps: rule.remediation_steps || '',
      enabled: rule.enabled,
      auto_remediate: rule.auto_remediate,
    });
    setModalOpen(true);
  };

  const duplicateRule = (rule: ComplianceRule) => {
    setEditingRule(null);
    setFormData({
      title: `${rule.title} (Copy)`,
      description: rule.description || '',
      category: rule.category,
      check_type: rule.check_type,
      severity: rule.severity,
      platform: rule.platform || 'all',
      device_type: rule.device_type || 'all',
      benchmark_ref: rule.benchmark_ref || '',
      check_command: rule.check_command || '',
      osquery_query_name: rule.osquery_query_name || '',
      expected_value: rule.expected_value || '',
      remediation_script: rule.remediation_script || '',
      remediation_steps: rule.remediation_steps || '',
      enabled: rule.enabled,
      auto_remediate: rule.auto_remediate,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.title.trim()) { setError('Title is required'); return; }
    setSaving(true);
    setError(null);
    try {
      const payload: Record<string, any> = { ...formData };
      Object.keys(payload).forEach((k) => { if (payload[k] === '') delete payload[k]; });
      payload.title = formData.title;
      payload.category = formData.category;
      payload.check_type = formData.check_type;
      payload.enabled = formData.enabled;
      payload.auto_remediate = formData.auto_remediate;

      if (editingRule) {
        await apiService.updateComplianceRule(editingRule.rule_id, payload);
        setSuccessMsg(`Rule "${formData.title}" updated`);
      } else {
        await apiService.createComplianceRule(payload as any);
        setSuccessMsg(`Rule "${formData.title}" created`);
      }
      setModalOpen(false);
      await loadRules();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save rule');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEnabled = async (rule: ComplianceRule) => {
    try {
      await apiService.updateComplianceRule(rule.rule_id, { enabled: !rule.enabled });
      setRules((prev) => prev.map((r) => (r.rule_id === rule.rule_id ? { ...r, enabled: !r.enabled } : r)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle rule');
    }
  };

  const handleDelete = async (ruleId: string) => {
    try {
      await apiService.deleteComplianceRule(ruleId);
      setRules((prev) => prev.filter((r) => r.rule_id !== ruleId));
      setDeleteConfirm(null);
      setSuccessMsg('Rule deleted');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete rule');
    }
  };

  const severityBadge = (sev: string) => {
    const s = SEVERITIES.find((x) => x.id === sev);
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${s?.color || 'bg-slate-100 text-slate-600'}`}>
        {sev}
      </span>
    );
  };

  const categoryBadge = (cat: string) => {
    const c = CATEGORIES.find((x) => x.id === cat);
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${c?.color || 'bg-slate-100 text-slate-600'}`}>
        {c?.name || cat}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <Shield size={20} className="text-blue-600" />
            Compliance Rules Library
          </h3>
          <p className="text-sm text-slate-500 mt-1">
            Configure rules for the 5 compliance categories. {rules.length} rules total.
          </p>
        </div>
        <button
          type="button"
          onClick={openCreateModal}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium shrink-0"
        >
          <Plus size={16} /> Add Rule
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <AlertTriangle size={16} /> {error}
          <button type="button" onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600"><X size={14} /></button>
        </div>
      )}
      {successMsg && (
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
          <CheckCircle2 size={16} /> {successMsg}
        </div>
      )}

      {/* Category summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {CATEGORIES.map((cat) => {
          const count = categoryCounts[cat.id] || 0;
          const isActive = filterCategory === cat.id;
          return (
            <button
              key={cat.id}
              type="button"
              onClick={() => setFilterCategory(isActive ? 'all' : cat.id)}
              className={`p-3 rounded-lg border text-left transition-all ${
                isActive
                  ? 'border-blue-400 bg-blue-50 ring-1 ring-blue-300'
                  : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
              }`}
            >
              <div className="text-xs font-medium text-slate-500 uppercase tracking-wider">{cat.name}</div>
              <div className="text-2xl font-bold text-slate-800 mt-1">{count}</div>
              <div className="text-xs text-slate-400">rules</div>
            </button>
          );
        })}
      </div>

      {/* Filters bar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search rules..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <select
          value={filterSeverity}
          onChange={(e) => setFilterSeverity(e.target.value)}
          className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Severities</option>
          {SEVERITIES.map((s) => (
            <option key={s.id} value={s.id}>{s.id.charAt(0).toUpperCase() + s.id.slice(1)}</option>
          ))}
        </select>
        <select
          value={filterEnabled}
          onChange={(e) => setFilterEnabled(e.target.value)}
          className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All States</option>
          <option value="enabled">Enabled</option>
          <option value="disabled">Disabled</option>
        </select>
        <span className="text-sm text-slate-500">{filteredRules.length} rules</span>
      </div>

      {/* Rules table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-slate-500">
            <span className="inline-block w-5 h-5 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
            Loading rules...
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 font-semibold uppercase tracking-wider text-xs">
                <tr>
                  <th className="px-4 py-3 w-10">On</th>
                  <th className="px-4 py-3">Title</th>
                  <th className="px-4 py-3">Category</th>
                  <th className="px-4 py-3">Check Type</th>
                  <th className="px-4 py-3">Severity</th>
                  <th className="px-4 py-3">Platform</th>
                  <th className="px-4 py-3">Benchmark</th>
                  <th className="px-4 py-3">Auto-Fix</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredRules.map((rule) => (
                  <React.Fragment key={rule.rule_id}>
                    <tr className={`hover:bg-slate-50 transition-colors ${!rule.enabled ? 'opacity-50' : ''}`}>
                      <td className="px-4 py-3">
                        <button type="button" onClick={() => handleToggleEnabled(rule)} title={rule.enabled ? 'Disable' : 'Enable'}>
                          {rule.enabled ? (
                            <ToggleRight size={22} className="text-blue-600" />
                          ) : (
                            <ToggleLeft size={22} className="text-slate-300" />
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => setExpandedRuleId(expandedRuleId === rule.rule_id ? null : rule.rule_id)}
                          className="flex items-center gap-1.5 text-left font-medium text-slate-800 hover:text-blue-600"
                        >
                          {expandedRuleId === rule.rule_id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                          {rule.title}
                        </button>
                      </td>
                      <td className="px-4 py-3">{categoryBadge(rule.category)}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600">
                          {rule.check_type}
                        </span>
                      </td>
                      <td className="px-4 py-3">{severityBadge(rule.severity)}</td>
                      <td className="px-4 py-3 text-slate-600 text-xs">{rule.platform || 'all'}</td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-500">{rule.benchmark_ref || '—'}</td>
                      <td className="px-4 py-3">
                        {rule.auto_remediate ? (
                          <span className="flex items-center gap-1 text-xs font-medium text-green-600"><Wrench size={12} /> Yes</span>
                        ) : (
                          <span className="text-xs text-slate-400">No</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            onClick={() => openEditModal(rule)}
                            className="p-1.5 rounded hover:bg-blue-50 text-slate-400 hover:text-blue-600"
                            title="Edit rule"
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={() => duplicateRule(rule)}
                            className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600"
                            title="Duplicate rule"
                          >
                            <Copy size={14} />
                          </button>
                          {deleteConfirm === rule.rule_id ? (
                            <div className="flex items-center gap-1">
                              <button
                                type="button"
                                onClick={() => handleDelete(rule.rule_id)}
                                className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                              >
                                Confirm
                              </button>
                              <button
                                type="button"
                                onClick={() => setDeleteConfirm(null)}
                                className="px-2 py-1 text-xs bg-slate-200 text-slate-600 rounded hover:bg-slate-300"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setDeleteConfirm(rule.rule_id)}
                              className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-600"
                              title="Delete rule"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {/* Expanded details */}
                    {expandedRuleId === rule.rule_id && (
                      <tr>
                        <td colSpan={9} className="px-4 py-4 bg-slate-50/70">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            {rule.description && (
                              <div className="md:col-span-2">
                                <label className="text-xs font-semibold text-slate-500 uppercase">Description</label>
                                <p className="text-slate-700 mt-0.5">{rule.description}</p>
                              </div>
                            )}
                            {rule.check_command && (
                              <div>
                                <label className="text-xs font-semibold text-slate-500 uppercase">Check Command</label>
                                <pre className="mt-1 p-2 bg-slate-800 text-green-300 rounded text-xs overflow-x-auto font-mono">{rule.check_command}</pre>
                              </div>
                            )}
                            {rule.expected_value && (
                              <div>
                                <label className="text-xs font-semibold text-slate-500 uppercase">Expected Value</label>
                                <pre className="mt-1 p-2 bg-slate-100 text-slate-700 rounded text-xs overflow-x-auto font-mono">{rule.expected_value}</pre>
                              </div>
                            )}
                            {rule.osquery_query_name && (
                              <div>
                                <label className="text-xs font-semibold text-slate-500 uppercase">Osquery Query Name</label>
                                <code className="block mt-1 px-2 py-1 bg-slate-100 rounded text-xs text-slate-700">{rule.osquery_query_name}</code>
                              </div>
                            )}
                            {rule.remediation_script && (
                              <div>
                                <label className="text-xs font-semibold text-slate-500 uppercase">Remediation Script</label>
                                <pre className="mt-1 p-2 bg-slate-800 text-amber-300 rounded text-xs overflow-x-auto font-mono">{rule.remediation_script}</pre>
                              </div>
                            )}
                            {rule.remediation_steps && (
                              <div>
                                <label className="text-xs font-semibold text-slate-500 uppercase">Remediation Steps</label>
                                <p className="text-slate-700 mt-0.5 whitespace-pre-line">{rule.remediation_steps}</p>
                              </div>
                            )}
                            <div className="flex gap-6 text-xs text-slate-400">
                              <span>Device Type: {rule.device_type || 'all'}</span>
                              {rule.created_at && <span>Created: {new Date(rule.created_at).toLocaleDateString()}</span>}
                              {rule.updated_at && <span>Updated: {new Date(rule.updated_at).toLocaleDateString()}</span>}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            {filteredRules.length === 0 && !loading && (
              <div className="py-12 text-center text-slate-500 text-sm">
                {rules.length === 0 ? 'No compliance rules configured. Click "Add Rule" to create one.' : 'No rules match the current filters.'}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create / Edit Modal */}
      {modalOpen && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setModalOpen(false)} />
          <div className="fixed inset-y-0 right-0 z-50 w-full max-w-2xl bg-white shadow-2xl border-l border-slate-200 overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
              <h3 className="text-lg font-semibold text-slate-800">
                {editingRule ? 'Edit Rule' : 'Create New Rule'}
              </h3>
              <button type="button" onClick={() => setModalOpen(false)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-5">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., SSH Root Login Disabled"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  placeholder="Brief description of what this rule checks"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Category + Check Type */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Category *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Check Type *</label>
                  <select
                    value={formData.check_type}
                    onChange={(e) => setFormData({ ...formData, check_type: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  >
                    {CHECK_TYPES.map((ct) => (
                      <option key={ct} value={ct}>{ct.charAt(0).toUpperCase() + ct.slice(1).replace('-', ' ')}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Severity + Platform + Device Type */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Severity</label>
                  <select
                    value={formData.severity}
                    onChange={(e) => setFormData({ ...formData, severity: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  >
                    {SEVERITIES.map((s) => (
                      <option key={s.id} value={s.id}>{s.id.charAt(0).toUpperCase() + s.id.slice(1)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Platform</label>
                  <select
                    value={formData.platform}
                    onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  >
                    {PLATFORMS.map((p) => (
                      <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Device Type</label>
                  <select
                    value={formData.device_type}
                    onChange={(e) => setFormData({ ...formData, device_type: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  >
                    {DEVICE_TYPES.map((dt) => (
                      <option key={dt} value={dt}>{dt.charAt(0).toUpperCase() + dt.slice(1)}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Benchmark Ref */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Benchmark Reference</label>
                <input
                  type="text"
                  value={formData.benchmark_ref}
                  onChange={(e) => setFormData({ ...formData, benchmark_ref: e.target.value })}
                  placeholder="e.g., CIS-5.2.10, NIST-AC-2, PCI-DSS-8.1"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <hr className="border-slate-200" />

              {/* Check Command */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Check Command</label>
                <p className="text-xs text-slate-400 mb-1.5">Shell command the agent runs to evaluate compliance. The output is compared to "Expected Value".</p>
                <textarea
                  value={formData.check_command}
                  onChange={(e) => setFormData({ ...formData, check_command: e.target.value })}
                  rows={3}
                  placeholder="e.g., grep -i PermitRootLogin /etc/ssh/sshd_config | head -1"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500 bg-slate-50"
                />
              </div>

              {/* Osquery Query Name */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Osquery Query Name (optional)</label>
                <p className="text-xs text-slate-400 mb-1.5">If set, the evaluator uses osquery snapshot data instead of running a command.</p>
                <input
                  type="text"
                  value={formData.osquery_query_name}
                  onChange={(e) => setFormData({ ...formData, osquery_query_name: e.target.value })}
                  placeholder="e.g., firewall_status"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500 bg-slate-50"
                />
              </div>

              {/* Expected Value */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Expected Value</label>
                <p className="text-xs text-slate-400 mb-1.5">The value the check command or osquery result should match for a "pass".</p>
                <input
                  type="text"
                  value={formData.expected_value}
                  onChange={(e) => setFormData({ ...formData, expected_value: e.target.value })}
                  placeholder="e.g., PermitRootLogin no"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500 bg-slate-50"
                />
              </div>

              <hr className="border-slate-200" />

              {/* Remediation Script */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Remediation Script</label>
                <p className="text-xs text-slate-400 mb-1.5">Script that the agent runs to auto-fix non-compliance when "Remediate" is clicked.</p>
                <textarea
                  value={formData.remediation_script}
                  onChange={(e) => setFormData({ ...formData, remediation_script: e.target.value })}
                  rows={3}
                  placeholder="e.g., sed -i 's/^PermitRootLogin.*/PermitRootLogin no/' /etc/ssh/sshd_config && systemctl restart sshd"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500 bg-slate-50"
                />
              </div>

              {/* Remediation Steps */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Remediation Steps (human-readable)</label>
                <textarea
                  value={formData.remediation_steps}
                  onChange={(e) => setFormData({ ...formData, remediation_steps: e.target.value })}
                  rows={3}
                  placeholder="1. Edit /etc/ssh/sshd_config&#10;2. Set PermitRootLogin no&#10;3. Restart sshd"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <hr className="border-slate-200" />

              {/* Toggles */}
              <div className="flex flex-wrap items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.enabled}
                    onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                    className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-slate-700">Enabled</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.auto_remediate}
                    onChange={(e) => setFormData({ ...formData, auto_remediate: e.target.checked })}
                    className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-slate-700">Auto-Remediate</span>
                  <span className="text-xs text-slate-400">(allow one-click fix from Compliance Hub)</span>
                </label>
              </div>
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="px-4 py-2 text-sm text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || !formData.title.trim()}
                className="px-5 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
              >
                {saving ? 'Saving...' : editingRule ? 'Update Rule' : 'Create Rule'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ComplianceRulesSettings;
